import { useEffect } from 'react';
import { Keyboard, KeyboardInfo, KeyboardResize } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';

/**
 * Keyboard Configuration Hook
 *
 * Provides native keyboard handling for better mobile UX:
 * - Adjusts layout when keyboard appears/disappears
 * - Sets CSS variable --keyboard-height for responsive layouts
 * - Configures iOS accessory bar
 * - Handles resize behavior
 *
 * Usage: Call once in root component (App.tsx)
 */
export const useKeyboardConfig = () => {
  useEffect(() => {
    // Only run on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Configure keyboard behavior
    const configureKeyboard = async () => {
      try {
        // iOS: Hide native accessory bar (up/down + done) to prevent it overlapping content
        await Keyboard.setAccessoryBarVisible({ isVisible: false });

        // Set resize mode to adjust layout when keyboard shows
        await Keyboard.setResizeMode({ mode: KeyboardResize.Native });

        // Set scroll assist (helps scroll to focused inputs)
        await Keyboard.setScroll({ isDisabled: false });
      } catch (error) {
        console.error('Failed to configure keyboard:', error);
      }
    };

    configureKeyboard();

    // Store listener handles for cleanup
    let showListenerHandle: { remove: () => Promise<void> } | null = null;
    let hideListenerHandle: { remove: () => Promise<void> } | null = null;

    // Listen for keyboard show/hide events
    const setupListeners = async () => {
      showListenerHandle = await Keyboard.addListener('keyboardWillShow', (info: KeyboardInfo) => {
        // Set CSS variable for keyboard height
        document.documentElement.style.setProperty(
          '--keyboard-height',
          `${info.keyboardHeight}px`
        );

        // Add class for keyboard-aware styling
        document.documentElement.classList.add('keyboard-visible');
      });

      hideListenerHandle = await Keyboard.addListener('keyboardWillHide', () => {
        // Reset CSS variable
        document.documentElement.style.setProperty('--keyboard-height', '0px');

        // Remove class
        document.documentElement.classList.remove('keyboard-visible');
      });
    };

    setupListeners();

    // Cleanup
    return () => {
      showListenerHandle?.remove();
      hideListenerHandle?.remove();
    };
  }, []);
};
